import { useTranslation } from "react-i18next";
import { Reply, Smile } from "lucide-react";
import { type InboxMessage, showReaction } from "@/lib/inbox";
import { formatShortTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { MessageMedia } from "./MessageMedia";
import { ReactionPicker } from "./ReactionPicker";

export interface InboxMessageBubbleProps {
  message: Pick<
    InboxMessage,
    | "id"
    | "direction"
    | "text"
    | "has_media"
    | "sent_at"
    | "was_replied_by_human"
    | "reply_to"
    | "sent_by"
    | "media_kind"
    | "media_name"
    | "my_reaction"
    | "peer_reaction"
  >;
  /** The dialog the message belongs to — its file is fetched through it. */
  dialogId: string;
  /** The other side's name — who a quoted incoming message is from. */
  peerName: string;
  /** Answer this very message. Without it the bubble offers no such action. */
  onReply?: () => void;
  /** Put a reaction on the message, or take it off (null). */
  onReact?: (emoji: string | null) => void;
}

// How the server names a reaction made with a custom emoji — there is no
// character to show for it.
const CUSTOM_REACTION = "custom";

// An action beside the bubble: under a mouse while the row is hovered, on a
// touch screen always.
const ACTION =
  "shrink-0 rounded-md p-1 text-fg-muted opacity-0 transition-opacity hover:bg-bg-elevated hover:text-fg-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:none)]:opacity-100";

/**
 * A single chat bubble. Shared by InboxPage and InboxDrawer. Outgoing
 * messages align right with an accent tint. Min font 12px — timestamps use
 * `text-xs`.
 *
 * A message that answers another carries its quote on top; what it carries (a
 * photo, a voice note, a file) is named and opens on a click; the reactions on
 * it sit under the bubble — the other side's (to look at) and our account's
 * (a click takes it off). The actions beside the bubble show under a mouse
 * while it is hovered, on a touch screen always.
 */
export function InboxMessageBubble({
  message,
  dialogId,
  peerName,
  onReply,
  onReact,
}: InboxMessageBubbleProps) {
  const { t } = useTranslation();
  const outgoing = message.direction === "out";
  const quote = message.reply_to;

  return (
    <div className={cn("group flex items-center gap-1.5", outgoing && "flex-row-reverse")}>
      <div className={cn("flex min-w-0 max-w-[80%] flex-col", outgoing ? "items-end" : "items-start")}>
        <div
          className={cn(
            "min-w-0 max-w-full rounded-xl px-3 py-2 text-sm",
            outgoing
              ? "border border-accent/30 bg-accent/15"
              : "border border-border bg-bg-elevated/60",
          )}
        >
          {quote && (
            <div className="mb-1.5 border-l-2 border-accent/60 pl-2 text-xs">
              <p className="font-medium text-accent">
                {quote.direction === "out"
                  ? t("inbox.reply.you")
                  : quote.direction === "in"
                    ? peerName
                    : t("inbox.reply.earlier")}
              </p>
              {quote.direction !== null && (
                <p className="line-clamp-2 break-words text-fg-muted">
                  {quote.text ?? (quote.has_media ? t("inbox.attached_media") : "—")}
                </p>
              )}
            </div>
          )}
          {message.has_media && <MessageMedia dialogId={dialogId} message={message} />}
          {(message.text || !message.has_media) && (
            <p className="whitespace-pre-wrap break-words">{message.text ?? "—"}</p>
          )}
          <p className="mt-1 flex items-center justify-end gap-1.5 text-xs text-fg-muted">
            {outgoing && message.was_replied_by_human && (
              <span className="min-w-0 truncate text-accent">
                {t("inbox.by_human")}
                {message.sent_by ? ` · ${message.sent_by}` : ""}
              </span>
            )}
            <span className="shrink-0 tabular-nums">{formatShortTime(message.sent_at)}</span>
          </p>
        </div>
        {(message.peer_reaction || message.my_reaction) && (
          <div className="-mt-1.5 mx-2 flex items-center gap-1">
            {message.peer_reaction && (
              <span
                role="img"
                aria-label={t("inbox.reaction.theirs", { name: peerName })}
                title={t("inbox.reaction.theirs", { name: peerName })}
                className="flex items-center rounded-full border border-border bg-bg-secondary px-1.5 py-0.5 text-sm leading-none"
              >
                {message.peer_reaction === CUSTOM_REACTION ? (
                  <Smile size={14} strokeWidth={2} aria-hidden="true" className="text-fg-muted" />
                ) : (
                  showReaction(message.peer_reaction)
                )}
              </span>
            )}
            {message.my_reaction && (
              <button
                type="button"
                onClick={onReact && (() => onReact(null))}
                disabled={!onReact}
                aria-label={t("inbox.reaction.remove", { emoji: message.my_reaction })}
                title={t("inbox.reaction.remove", { emoji: message.my_reaction })}
                className="rounded-full border border-accent/40 bg-bg-secondary px-1.5 py-0.5 text-sm leading-none transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {showReaction(message.my_reaction)}
              </button>
            )}
          </div>
        )}
      </div>
      {onReact && (
        <ReactionPicker
          current={message.my_reaction}
          onPick={onReact}
          className={cn(ACTION, "data-[state=open]:opacity-100")}
        />
      )}
      {onReply && (
        <button
          type="button"
          onClick={onReply}
          aria-label={t("inbox.reply.action")}
          title={t("inbox.reply.action")}
          className={ACTION}
        >
          <Reply size={15} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
InboxMessageBubble.displayName = "InboxMessageBubble";
